import { useState, useEffect, useCallback } from 'react';
import { BookOpen, ChevronLeft, AlertCircle, Users } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray50: '#F9FAFB', gray100: '#F3F4F6',
  gray400: '#9CA3AF', gray500: '#6B7280', gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#DC2626', errorBg: '#FEE2E2', errorText: '#991B1B',
};
const cardShadow = '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';

const T = {
  ms: {
    title: 'Kemasukan Markah', subtitle: 'Gred huruf diterbitkan automatik dari skala Brunei.',
    changeSelection: 'Tukar Pilihan', classLabel: 'Kelas', subjectLabel: 'Subjek',
    termLabel: 'Peperiksaan', typeLabel: 'Jenis Penilaian', maxScoreLabel: 'Markah Penuh',
    startLoading: 'Memuatkan…', startButton: 'Mula Kemasukan Markah',
    scaletitle: 'Skala Gred Brunei',
    colStudent: 'Pelajar', colScore: 'Markah', colGrade: 'Gred', colNotes: 'Catatan',
    notesPh: 'Catatan…', noStudents: 'Tiada pelajar aktif dijumpai.',
    enteredOf: 'dimasukkan daripada', gradeDerived: 'Gred diterbitkan automatik',
    saving: 'Menyimpan…', saveButton: 'Simpan Markah',
    errLabel: 'Ralat', retry: 'Cuba Lagi', noScores: 'Tiada markah dimasukkan.',
  },
  en: {
    title: 'Grade Entry', subtitle: 'Grade letter is auto-derived from the Brunei national grading scale.',
    changeSelection: 'Change Selection', classLabel: 'Class', subjectLabel: 'Subject',
    termLabel: 'Term', typeLabel: 'Assessment Type', maxScoreLabel: 'Max Score',
    startLoading: 'Loading…', startButton: 'Start Entry',
    scaletitle: 'Brunei Grading Scale',
    colStudent: 'Student', colScore: 'Score', colGrade: 'Grade', colNotes: 'Notes',
    notesPh: 'Notes…', noStudents: 'No active students found.',
    enteredOf: 'entered of', gradeDerived: 'Grade auto-derived from Brunei scale',
    saving: 'Saving…', saveButton: 'Save Grades',
    errLabel: 'Error', retry: 'Retry', noScores: 'No scores entered.',
  },
};

interface Props { user: AuthUser }
interface SchoolClass { id: string; name: string; level: string; school_id: string }
interface GradingScale { grade_letter: string; min_pct: number; max_pct: number; gpa_points: number }
interface Student { id: string; full_name: string; registration_number: string }
interface BulkResult { success: number; errors: Array<{ studentId: string; reason: string }> }

const DEMO_SUBJECTS = [
  { id: '00000000-0000-0000-0000-000000000001', name: 'Bahasa Melayu' },
  { id: '00000000-0000-0000-0000-000000000002', name: 'English Language' },
  { id: '00000000-0000-0000-0000-000000000003', name: 'Mathematics' },
  { id: '00000000-0000-0000-0000-000000000004', name: 'Science' },
  { id: '00000000-0000-0000-0000-000000000005', name: 'Pendidikan Islam' },
  { id: '00000000-0000-0000-0000-000000000006', name: 'Sejarah' },
];

const TERMS   = ['T1', 'T2', 'T3', 'MID', 'FINAL'] as const;
const A_TYPES = ['exam', 'test', 'quiz', 'homework', 'project'] as const;

const GRADE_COLOURS: Record<string, { bg: string; text: string }> = {
  'A*': { bg: '#DCFCE7', text: '#166534' }, A: { bg: '#DCFCE7', text: '#166534' },
  'B+': { bg: '#D1FAE5', text: '#065F46' }, B: { bg: '#FEF3C7', text: '#92400E' },
  'C+': { bg: '#FEF3C7', text: '#92400E' }, C: { bg: '#FFEDD5', text: '#9A3412' },
  D:   { bg: '#FEE2E2', text: '#991B1B' },  E: { bg: '#FEE2E2', text: '#991B1B' },
  U:   { bg: '#F3F4F6', text: '#6B7280' },
};

function getGradeColour(letter: string) { return GRADE_COLOURS[letter] ?? GRADE_COLOURS['U']!; }

function deriveGrade(pct: number, scales: GradingScale[]): GradingScale | null {
  return [...scales].sort((a, b) => b.min_pct - a.min_pct).find((s) => pct >= s.min_pct) ?? null;
}

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

const sharedInputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: C.white, border: `1.5px solid ${C.border}`,
  borderRadius: '8px', color: C.gray900, padding: '9px 12px',
  fontSize: '14px', outline: 'none', fontFamily: '"Inter", sans-serif',
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '6px' }}>
      {children}
    </label>
  );
}

export function GradesPage({ user }: Props) {
  const { lang } = useLang();
  const UI = T[lang === 'en' ? 'en' : 'ms'];
  usePageTitle(lang === 'en' ? 'Grade Entry' : 'Kemasukan Markah');

  const schoolId = getSchoolId(user);
  const effectiveSchoolId = schoolId ?? '3f868b12-b554-4f51-a893-69e9b4ba9f57';

  const [classes,       setClasses]       = useState<SchoolClass[]>([]);
  const [scales,        setScales]        = useState<GradingScale[]>([]);
  const [students,      setStudents]      = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubj,  setSelectedSubj]  = useState(DEMO_SUBJECTS[0]!.id);
  const [selectedTerm,  setSelectedTerm]  = useState<typeof TERMS[number]>('T1');
  const [assessType,    setAssessType]    = useState<typeof A_TYPES[number]>('exam');
  const [maxScore,      setMaxScore]      = useState(100);
  const [schoolYearId,  setSchoolYearId]  = useState('');
  const [scores,        setScores]        = useState<Record<string, string>>({});
  const [remarks,       setRemarks]       = useState<Record<string, string>>({});
  const [phase,         setPhase]         = useState<'pick' | 'enter'>('pick');
  const [classesLoading,  setClassesLoading]  = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [submitting,      setSubmitting]      = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [toast,           setToast]           = useState<{ message: string; variant: ToastVariant } | null>(null);

  const loadClasses = useCallback(async () => {
    setClassesLoading(true); setError(null);
    try {
      const [cls, sc] = await Promise.all([
        apiGet<SchoolClass[]>(`/api/students/classes?schoolId=${effectiveSchoolId}`),
        apiGet<GradingScale[]>(`/api/students/grades/scales?levelType=secondary`),
      ]);
      setClasses(cls); setScales(sc);
      if (cls.length > 0 && !selectedClass) setSelectedClass(cls[0]!.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : lang === 'en' ? 'Unknown error.' : 'Ralat tidak diketahui.');
    } finally { setClassesLoading(false); }
  }, [effectiveSchoolId, selectedClass, lang]);

  useEffect(() => { void loadClasses(); }, []); // eslint-disable-line

  async function handleStartEntry() {
    if (!selectedClass || studentsLoading) return;
    setStudentsLoading(true); setError(null);
    try {
      const data = await apiGet<{ students: Student[] } | Student[]>(
        `/api/students/sessions/school-students?schoolId=${effectiveSchoolId}`,
      ).catch(async () => apiGet<Student[]>(`/api/students/list?schoolId=${effectiveSchoolId}`));
      const studs = Array.isArray(data) ? (data as Student[]) : (data as { students: Student[] }).students ?? [];
      setStudents(studs);

      const yearRows = await apiGet<Array<{ id: string }>>(`/api/students/school-year?schoolId=${effectiveSchoolId}`).catch(() => []);
      if (Array.isArray(yearRows) && yearRows[0]) setSchoolYearId(yearRows[0].id);

      const defaults: Record<string, string> = {};
      studs.forEach((s) => { defaults[s.id] = ''; });
      setScores(defaults);
      setPhase('enter');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat semasa memuatkan pelajar.');
    } finally { setStudentsLoading(false); }
  }

  async function handleSubmit() {
    if (submitting) return;
    const entries = students
      .filter((s) => scores[s.id]?.trim() !== '')
      .map((s) => ({ studentId: s.id, score: parseFloat(scores[s.id] ?? '0'), remarks: remarks[s.id]?.trim() || undefined }));

    if (entries.length === 0) { setToast({ variant: 'error', message: UI.noScores }); return; }

    const bad = entries.find((e) => isNaN(e.score) || e.score < 0 || e.score > maxScore);
    if (bad) { setToast({ variant: 'error', message: `${lang === 'en' ? 'Invalid score (0–' : 'Markah tidak sah (0–'}${maxScore}).` }); return; }

    const subjObj = DEMO_SUBJECTS.find((s) => s.id === selectedSubj)!;
    setSubmitting(true);
    try {
      const result = await apiPost<BulkResult>('/api/students/grades/bulk', {
        classId: selectedClass, subjectId: selectedSubj, subjectName: subjObj.name,
        schoolYearId: schoolYearId || '00000000-0000-0000-0000-000000000000',
        term: selectedTerm, assessmentType: assessType, maxScore, entries,
      });
      const errMsg = result.errors.length > 0 ? ` · ${result.errors.length} errors` : '';
      setToast({ variant: result.errors.length === 0 ? 'success' : 'error', message: `${result.success} ${lang === 'en' ? 'grades saved' : 'gred disimpan'}${errMsg}` });
      if (result.success > 0) setPhase('pick');
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Ralat.' });
    } finally { setSubmitting(false); }
  }

  function previewGrade(scoreStr: string): { letter: string; pct: number } | null {
    const score = parseFloat(scoreStr);
    if (isNaN(score) || score < 0 || scoreStr.trim() === '') return null;
    const pct = Math.round((score / maxScore) * 10000) / 100;
    const scale = deriveGrade(pct, scales);
    return scale ? { letter: scale.grade_letter, pct } : null;
  }

  const enteredCount = Object.values(scores).filter((v) => v.trim() !== '').length;

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Breadcrumb */}
      {phase === 'enter' && (
        <button
          onClick={() => { setPhase('pick'); setStudents([]); setScores({}); }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: C.gray500, fontSize: '14px', padding: '0 0 16px', fontFamily: '"Inter", sans-serif' }}
        >
          <ChevronLeft size={16} /> {UI.changeSelection}
        </button>
      )}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: C.gray900, marginBottom: '4px', fontFamily: '"Noto Sans", sans-serif' }}>{UI.title}</h1>
        <p style={{ fontSize: '14px', color: C.gray500 }}>
          {phase === 'pick' ? UI.subtitle : `${enteredCount} ${UI.enteredOf} ${students.length}`}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ backgroundColor: C.errorBg, border: '1px solid #FECACA', borderLeft: `4px solid ${C.error}`, borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AlertCircle size={18} color={C.error} strokeWidth={2} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: C.errorText }}>{UI.errLabel}</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: C.errorText }}>{error}</p>
          </div>
          <button onClick={() => void loadClasses()} style={{ backgroundColor: C.error, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            {UI.retry}
          </button>
        </div>
      )}

      {/* ── Phase 1: Picker ── */}
      {phase === 'pick' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '28px', boxShadow: cardShadow }}>
            {classesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '48px' }}>
                <span style={{ width: '22px', height: '22px', border: `3px solid ${C.border}`, borderTopColor: C.yellow, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <span style={{ color: C.gray500, fontSize: '14px' }}>{lang === 'en' ? 'Loading classes…' : 'Memuatkan kelas…'}</span>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <FieldLabel>{UI.classLabel}</FieldLabel>
                  <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} style={sharedInputStyle} disabled={studentsLoading}>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>{UI.subjectLabel}</FieldLabel>
                  <select value={selectedSubj} onChange={(e) => setSelectedSubj(e.target.value)} style={sharedInputStyle} disabled={studentsLoading}>
                    {DEMO_SUBJECTS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>{UI.termLabel}</FieldLabel>
                  <select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value as typeof TERMS[number])} style={sharedInputStyle} disabled={studentsLoading}>
                    {TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>{UI.typeLabel}</FieldLabel>
                  <select value={assessType} onChange={(e) => setAssessType(e.target.value as typeof A_TYPES[number])} style={sharedInputStyle} disabled={studentsLoading}>
                    {A_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>{UI.maxScoreLabel}</FieldLabel>
                  <input type="number" value={maxScore} min={1} max={1000}
                    onChange={(e) => setMaxScore(Math.max(1, Math.min(1000, Number(e.target.value) || 100)))}
                    style={sharedInputStyle} disabled={studentsLoading}
                  />
                </div>
              </div>
            )}

            <button
              onClick={() => void handleStartEntry()}
              disabled={!selectedClass || classesLoading || studentsLoading}
              style={{
                marginTop: '24px', width: '100%',
                backgroundColor: C.yellow, color: C.black,
                border: 'none', borderRadius: '8px', padding: '12px 20px',
                fontSize: '15px', fontWeight: 700, cursor: (!selectedClass || classesLoading || studentsLoading) ? 'not-allowed' : 'pointer',
                opacity: (!selectedClass || classesLoading || studentsLoading) ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {studentsLoading
                ? <><span style={{ width: '16px', height: '16px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: C.black, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />{UI.startLoading}</>
                : <><BookOpen size={18} strokeWidth={2} />{UI.startButton}</>
              }
            </button>
          </div>

          {/* Grading scale */}
          {scales.length > 0 && (
            <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '20px', boxShadow: cardShadow }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                {UI.scaletitle}
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {scales.map((s) => {
                  const gc = getGradeColour(s.grade_letter);
                  return (
                    <div key={s.grade_letter} style={{ textAlign: 'center', padding: '8px 12px', backgroundColor: gc.bg, borderRadius: '8px', minWidth: '56px' }}>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: gc.text }}>{s.grade_letter}</div>
                      <div style={{ fontSize: '11px', color: C.gray500, marginTop: '2px' }}>{s.min_pct}–{s.max_pct}%</div>
                      <div style={{ fontSize: '10px', color: C.gray400 }}>{s.gpa_points} GPA</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Phase 2: Grade entry ── */}
      {phase === 'enter' && (
        <>
          {students.length === 0 ? (
            <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '64px', textAlign: 'center', boxShadow: cardShadow }}>
              <Users size={40} color={C.gray400} style={{ marginBottom: '12px' }} />
              <p style={{ color: C.gray500 }}>{UI.noStudents}</p>
            </div>
          ) : (
            <>
              <div style={{ backgroundColor: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: cardShadow, marginBottom: '16px' }}>
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 80px 140px', gap: '8px', padding: '10px 20px', backgroundColor: C.gray50, borderBottom: `1px solid ${C.border}` }}>
                  {[UI.colStudent, `${UI.colScore} (/${maxScore})`, UI.colGrade, UI.colNotes].map((h) => (
                    <span key={h} style={{ fontSize: '12px', fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
                  ))}
                </div>

                {students.map((student, i) => {
                  const preview    = previewGrade(scores[student.id] ?? '');
                  const scoreVal   = scores[student.id] ?? '';
                  const isInvalid  = scoreVal !== '' && (isNaN(parseFloat(scoreVal)) || parseFloat(scoreVal) < 0 || parseFloat(scoreVal) > maxScore);
                  const gc         = preview ? getGradeColour(preview.letter) : null;

                  return (
                    <div
                      key={student.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 130px 80px 140px',
                        gap: '8px', padding: '10px 20px',
                        borderBottom: i < students.length - 1 ? `1px solid ${C.border}` : 'none',
                        alignItems: 'center', opacity: submitting ? 0.6 : 1,
                        backgroundColor: i % 2 === 0 ? C.white : C.gray50,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: C.gray900 }}>{student.full_name}</div>
                        <div style={{ fontSize: '12px', color: C.gray400 }}>{student.registration_number}</div>
                      </div>
                      <input
                        type="number" value={scoreVal} min={0} max={maxScore} step={0.5}
                        placeholder={`0–${maxScore}`} disabled={submitting}
                        onChange={(e) => setScores((prev) => ({ ...prev, [student.id]: e.target.value }))}
                        style={{ ...sharedInputStyle, border: `1.5px solid ${isInvalid ? C.error : C.border}`, padding: '8px 10px' }}
                      />
                      <div style={{ textAlign: 'center' }}>
                        {preview && gc
                          ? <span style={{ backgroundColor: gc.bg, color: gc.text, padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700 }}>{preview.letter}</span>
                          : <span style={{ color: C.gray400, fontSize: '12px' }}>—</span>
                        }
                      </div>
                      <input
                        type="text" value={remarks[student.id] ?? ''} placeholder={UI.notesPh} disabled={submitting}
                        onChange={(e) => setRemarks((prev) => ({ ...prev, [student.id]: e.target.value }))}
                        style={{ ...sharedInputStyle, padding: '8px 10px' }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Sticky submit bar */}
              <div style={{ position: 'sticky', bottom: '0', backgroundColor: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px 20px', boxShadow: '0 -4px 12px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: '14px', color: C.gray700 }}>
                    <strong style={{ color: C.gray900 }}>{enteredCount}</strong> {UI.enteredOf} <strong style={{ color: C.gray900 }}>{students.length}</strong>
                  </span>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.gray500 }}>{UI.gradeDerived}</p>
                </div>
                <button
                  onClick={() => void handleSubmit()}
                  disabled={submitting || enteredCount === 0}
                  style={{
                    backgroundColor: enteredCount === 0 ? C.gray400 : C.yellow, color: C.black,
                    border: 'none', borderRadius: '8px', padding: '12px 24px',
                    fontSize: '15px', fontWeight: 700,
                    cursor: (submitting || enteredCount === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap',
                  }}
                >
                  {submitting
                    ? <><span style={{ width: '16px', height: '16px', border: '2px solid rgba(0,0,0,0.2)', borderTopColor: C.black, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />{UI.saving}</>
                    : <><BookOpen size={18} strokeWidth={2} />{UI.saveButton} ({enteredCount})</>
                  }
                </button>
              </div>
            </>
          )}
        </>
      )}

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </div>
  );
}
